import type { SsrAppApiDirectOptions } from '@/data/appApi';
import { Device } from '@/store/device/types';
import apiRequest from '@/utils/apiRequest';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';
import { getDeviceDataFromUserAgentString } from '@/utils/device';

export const getDevice = async (
	userAgent: string | null | undefined,
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
): Promise<Device> => {
	const deviceBasic = {
		deviceId: userData?.deviceId || undefined,
		...getDeviceDataFromUserAgentString(userAgent),
	};
	if (!userAgent) {
		return deviceBasic;
	}

	try {
		if (typeof window === 'undefined' && opts?.ctx) {
			// SSR app-api-direct: see getFeatureFlags.ts. `requiresAuth: true`
			// matches the authproxy (Bearer injected for /device), so a signed-in user keeps
			// their personalized device record; anonymous requests stay anonymous.
			const { appApiServerRequest } = await import('@/data/appApi');
			const { data } = await appApiServerRequest<Device>({
				endpoint: 'device',
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
			});
			return data ? { ...data, ...deviceBasic } : deviceBasic;
		}

		const result = await apiRequest({
			endpoint: 'device',
			method: 'GET',
			userData,
		});
		const data = result?.data;
		const device = {
			...data,
			...deviceBasic,
		};

		return !data ? deviceBasic : device;
	} catch (err) {
		calmLogger.error('Error retrieving device via GET /device', {}, err);
		return deviceBasic;
	}
};
